import React, { useState, useEffect } from "react";
import { Table, Tag } from "antd";
import moment from "moment";
import axios from "axios";
import Swal from "sweetalert2";
import { useTranslation } from "react-i18next";

const apiURL = process.env.REACT_APP_API_URL || "https://api.example.com";
const currency = localStorage.getItem("currencycode") || "₹";

const DailyRoomSold = ({ selectedDate, setSelectedDate, setExportData, setExportHeaders, setExportFooter, setTotalsForPageFooter, setTotalColumns, setReportTitle, parentFilters }) => {
  const { t } = useTranslation();
  const [filter, setFilter] = useState("ALL");
  const [selectedRoomTypes, setSelectedRoomTypes] = useState([]);
  const [roomTypes, setRoomTypes] = useState(["ALL"]);
  const [soldRooms, setSoldRooms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [applicableTaxes, setApplicableTaxes] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  useEffect(() => {
    fetchRoomTypes();
  }, []);

  useEffect(() => {
    fetchReport();
  }, [selectedDate, pagination.current, pagination.pageSize]);

  const fetchRoomTypes = async () => {
    const token = localStorage.getItem("token");
    try {
      const response = await axios.get(`${apiURL}/roomtype/listings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data && response.data.roomTypes) {
        const types = ["ALL", ...response.data.roomTypes.map((type) => type.type_name)];
        setRoomTypes(types);
      } else {
        throw new Error("Room types data not found in response");
      }
    } catch (error) {
      Swal.fire({
        title: t("error"),
        text: t("failed_to_fetch_room_types"),
        icon: "error",
      });
    }
  };

  const fetchReport = async () => {
    setLoading(true);
    const token = localStorage.getItem("token");
    try {
      const response = await axios.get(`${apiURL}/reports-daily/room-sold-report`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          date: selectedDate.format("YYYY-MM-DD"),
          page: pagination.current,
          pageSize: pagination.pageSize,
        },
      });
      if (response.data.success) {
        const formattedData = response.data.report.map((room, index) => ({
          room_id: index,
          room_number: room["Room No"],
          room_type: room["Room Type"],
          pax: room["Pax (Adults/Children)"],
          plan: room["Plan"],
          room_charges: parseFloat(room["Room Rent"]) || 0,
          applied_discount: parseFloat(room["Discount"]) || 0,
          sold_price: parseFloat(room["Price Sold"]) || 0,
          tax: room["Tax Amount"] ? parseFloat(room["Tax Amount"]) : 0,
          booking_source: room["Booking Source"],
          ...room["Tax Breakdown"]?.reduce((acc, tax) => ({
            ...acc,
            [tax.taxName]: tax.taxAmount
          }), {}),
        }));
        setSoldRooms(formattedData);
        setApplicableTaxes(response.data.applicableTaxes || []);
        setPagination({
          ...pagination,
          total: response.data.pagination.totalRecords,
        });
        if (formattedData.length === 0) {
          Swal.fire({
            title: t("no_data"),
            text: t("no_room_sold_data"),
            icon: "info",
          });
        }
      } else {
        Swal.fire({
          title: t("error"),
          text: t("failed_to_fetch_room_sold_report"),
          icon: "error",
        });
        setSoldRooms([]);
        setPagination({ ...pagination, total: 0 });
      }
    } catch (error) {
      console.error("Error fetching Room Sold Report:", error);
      Swal.fire({
        title: t("error"),
        text: t("failed_to_fetch_room_sold_report"),
        icon: "error",
      });
      setSoldRooms([]);
      setPagination({ ...pagination, total: 0 });
    } finally {
      setLoading(false);
    }
  };

  const handleTableChange = (pagination) => {
    setPagination({
      ...pagination,
      current: pagination.current,
      pageSize: pagination.pageSize,
    });
  };

  const filteredRooms = soldRooms.filter((room) => {
    const matchesRoomNo = parentFilters.roomNo ? room.room_number.toLowerCase().includes(parentFilters.roomNo.toLowerCase()) : true;
    const matchesRoomType = parentFilters.roomType.length > 0
      ? parentFilters.roomType.some((type) => type.value === room.room_type)
      : (filter === "ALL" && selectedRoomTypes.length === 0) || selectedRoomTypes.some((type) => type.value === room.room_type);
    const matchesBookingSource = parentFilters.bookingSource ? room.booking_source === parentFilters.bookingSource.value : true;
    const matchesStatus = parentFilters.reservationStatus ? room.status === parentFilters.reservationStatus.value : true;
    const matchesCreatedBy = parentFilters.createdBy ? room.created_by === parentFilters.createdBy.value : true;
    return matchesRoomNo && matchesRoomType && matchesBookingSource && matchesStatus && matchesCreatedBy;
  });

  const totals = filteredRooms.reduce(
    (acc, room) => {
      const taxBreakdownTotals = {};
      applicableTaxes.forEach((taxName) => {
        taxBreakdownTotals[taxName] = (acc[taxName] || 0) + (room[taxName] || 0);
      });
      return {
        roomCharges: acc.roomCharges + room.room_charges,
        appliedDiscount: acc.appliedDiscount + room.applied_discount,
        soldPrice: acc.soldPrice + room.sold_price,
        tax: acc.tax + room.tax,
        ...taxBreakdownTotals,
      };
    },
    {
      roomCharges: 0,
      appliedDiscount: 0,
      soldPrice: 0,
      tax: 0,
      ...applicableTaxes.reduce((acc, taxName) => ({ ...acc, [taxName]: 0 }), {}),
    }
  );

  const bookingSourceSummary = filteredRooms.reduce((acc, room) => {
    acc[room.booking_source] = (acc[room.booking_source] || 0) + 1;
    return acc;
  }, {});

  const dayBookStats = [
    { label: t("total_room_charges"), value: `${currency}${totals.roomCharges.toFixed(2)}`, color: "#007bff" },
    { label: t("total_discounts"), value: `${currency}${totals.appliedDiscount.toFixed(2)}`, color: "#dc3545" },
    { label: t("total_tax"), value: `${currency}${totals.tax.toFixed(2)}`, color: "#17a2b8" },
    { label: t("total_sold_price"), value: `${currency}${totals.soldPrice.toFixed(2)}`, color: "#28a745" },
    { label: t("direct_bookings"), value: `${bookingSourceSummary["Direct"] || 0}`, color: "#6f42c1" },
  ];

  const exportHeaders = [
    { key: "room_number", label: t("room_no") },
    { key: "room_type", label: t("room_type") },
    { key: "pax", label: t("pax_adults_children") },
    { key: "plan", label: t("plan") },
    { key: "room_charges", label: `${t("room_charges")} (${currency})` },
    { key: "applied_discount", label: `${t("applied_discount")} (${currency})` },
    ...applicableTaxes.map((taxName) => ({
      key: taxName,
      label: `${taxName} (${currency})`,
    })),
    { key: "tax", label: `${t("total_tax")} (${currency})` },
    { key: "sold_price", label: `${t("sold_price")} (${currency})` },
    { key: "booking_source", label: t("booking_source") },
  ];

  const exportData = filteredRooms.map((room) => {
    const taxBreakdownData = {};
    applicableTaxes.forEach((taxName) => {
      taxBreakdownData[taxName] = (room[taxName] || 0).toFixed(2);
    });
    return {
      room_number: room.room_number,
      room_type: room.room_type,
      pax: room.pax,
      plan: room.plan,
      room_charges: room.room_charges.toFixed(2),
      applied_discount: room.applied_discount.toFixed(2),
      ...taxBreakdownData,
      tax: room.tax.toFixed(2),
      sold_price: room.sold_price.toFixed(2),
      booking_source: room.booking_source,
    };
  });

  const totalsForPageFooter = [
    { key: t("total_rooms"), value: filteredRooms.length },
    { key: t("direct_bookings"), value: bookingSourceSummary["Direct"] || 0 },
  ];

  const exportFooter = [
    { key: "room_charges", value: totals.roomCharges, currency },
    { key: "applied_discount", value: totals.appliedDiscount, currency },
    ...applicableTaxes.map((taxName) => ({
      key: taxName,
      value: totals[taxName],
      currency,
    })),
    { key: "tax", value: totals.tax, currency },
    { key: "sold_price", value: totals.soldPrice, currency },
  ];

  // Update parent export state
  useEffect(() => {
    setExportData(exportData);
    setExportHeaders(exportHeaders);
    setExportFooter(exportFooter);
    setTotalsForPageFooter(totalsForPageFooter);
    setTotalColumns(["room_charges", "applied_discount", ...applicableTaxes, "tax", "sold_price"]);
    setReportTitle(t("daily_room_sold_report"));
  }, [filteredRooms, applicableTaxes, setExportData, setExportHeaders, setExportFooter, setTotalsForPageFooter, setTotalColumns, setReportTitle, t]);

  const columns = [
    {
      title: t("room_no"),
      dataIndex: "room_number",
      key: "room_number",
      sorter: (a, b) => a.room_number.localeCompare(b.room_number),
    },
    {
      title: t("room_type"),
      dataIndex: "room_type",
      key: "room_type",
    },
    {
      title: t("pax_adults_children"),
      dataIndex: "pax",
      key: "pax",
    },
    {
      title: t("plan"),
      dataIndex: "plan",
      key: "plan",
    },
    {
      title: t("room_charges"),
      dataIndex: "room_charges",
      key: "room_charges",
      render: (text) => `${currency}${text.toFixed(2)}`,
      sorter: (a, b) => a.room_charges - b.room_charges,
    },
    {
      title: t("applied_discount"),
      dataIndex: "applied_discount",
      key: "applied_discount",
      render: (text, record) => {
        const discountPercentage = record.room_charges ? (text / record.room_charges) * 100 : 0;
        return (
          <span style={{ color: discountPercentage > 20 ? "#ff4d4f" : "#000" }}>
            {currency}{text.toFixed(2)} {discountPercentage > 20 && <Tag color="red">{t("high_discount")}</Tag>}
          </span>
        );
      },
      sorter: (a, b) => a.applied_discount - b.applied_discount,
    },
    ...applicableTaxes.map((taxName) => ({
      title: `${taxName} (${currency})`,
      dataIndex: taxName,
      key: taxName,
      render: (text) => `${currency}${(text || 0).toFixed(2)}`,
      sorter: (a, b) => (a[taxName] || 0) - (b[taxName] || 0),
    })),
    {
      title: t("total_tax"),
      dataIndex: "tax",
      key: "tax",
      render: (text) => `${currency}${text.toFixed(2)}`,
      sorter: (a, b) => a.tax - b.tax,
    },
    {
      title: t("sold_price"),
      dataIndex: "sold_price",
      key: "sold_price",
      render: (text) => `${currency}${text.toFixed(2)}`,
      sorter: (a, b) => a.sold_price - b.sold_price,
      onCell: () => ({ style: { backgroundColor: "#e2e6ea" } }),
    },
    {
      title: t("booking_source"),
      dataIndex: "booking_source",
      key: "booking_source",
      filters: [...new Set(soldRooms.map((room) => room.booking_source))].map((source) => ({
        text: source,
        value: source,
      })),
      onFilter: (value, record) => record.booking_source === value,
    },
  ];

  return (
    <div className="daily-room-sold-container">
      <div className="row g-4 mb-4" style={{ padding: "5px 10px" }}>
        {dayBookStats.map((stat, index) => (
          <div
            key={index}
            className="col-md-2 col-6 stats-box"
            style={{ margin: "0 15px", borderRadius: "8px", boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)", padding: "10px", position: "relative" }}
          >
            <div
              className="stats-border"
              style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: "4px", borderRadius: "4px 0 0 4px", backgroundColor: stat.color }}
            ></div>
            <div className="stats-content" style={{ paddingLeft: "10px" }}>
              <h2 style={{ fontSize: "1.5rem", margin: 0 }}>{stat.value}</h2>
              <p style={{ margin: 0, color: "#666" }}>{stat.label}</p>
            </div>
          </div>
        ))}
      </div>
      <div className="table-responsive" style={{ backgroundColor: "#fff", borderRadius: "8px", boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)", padding: "10px" }}>
        {loading ? (
          <div className="text-center my-5">
            <div className="loader">
              <div className="dot"></div>
              <div className="dot"></div>
              <div className="dot"></div>
              <div className="dot"></div>
              <div className="dot"></div>
            </div>
          </div>
        ) : filteredRooms.length === 0 ? (
          <div className="no-data-container text-center mt-4" style={{ height: "100%", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center" }}>
            <img
              src="https://ui-cdn.digitalocean.com/apps/ui-spaces/static/media/spaces-empty.5e1d7947c43b7e336b14e421d4a86639.svg"
              alt="No data available"
              style={{ width: "150px", marginBottom: "20px" }}
            />
            <h3>{t("no_data")}</h3>
            <p>{t("no_room_sold_data")}</p>
          </div>
        ) : (
          <Table
            dataSource={filteredRooms}
            columns={columns}
            rowKey="room_id"
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: true,
              pageSizeOptions: ["10", "20", "50", "100"],
              onChange: (page, pageSize) => handleTableChange({ current: page, pageSize }),
              onShowSizeChange: (current, size) => handleTableChange({ current: 1, pageSize: size }),
            }}
            summary={() => (
              <Table.Summary.Row style={{ backgroundColor: "#e6f4ea", fontWeight: "700", fontSize: "15px" }}>
                <Table.Summary.Cell colSpan={4}>{t("total")}</Table.Summary.Cell>
                <Table.Summary.Cell>{currency}{totals.roomCharges.toFixed(2)}</Table.Summary.Cell>
                <Table.Summary.Cell>{currency}{totals.appliedDiscount.toFixed(2)}</Table.Summary.Cell>
                {applicableTaxes.map((taxName, index) => (
                  <Table.Summary.Cell key={index}>{currency}{totals[taxName].toFixed(2)}</Table.Summary.Cell>
                ))}
                <Table.Summary.Cell>{currency}{totals.tax.toFixed(2)}</Table.Summary.Cell>
                <Table.Summary.Cell style={{ backgroundColor: "#e2e6ea" }}>{currency}{totals.soldPrice.toFixed(2)}</Table.Summary.Cell>
                <Table.Summary.Cell></Table.Summary.Cell>
              </Table.Summary.Row>
            )}
          />
        )}
      </div>
      <style jsx>{`
        .loader {
          display: flex;
          justify-content: center;
          align-items: center;
        }
        .dot {
          width: 10px;
          height: 10px;
          background-color: #007bff;
          border-radius: 50%;
          margin: 0 5px;
          animation: dot-bounce 1.4s infinite ease-in-out both;
        }
        .dot:nth-child(1) { animation-delay: -0.32s; }
        .dot:nth-child(2) { animation-delay: -0.16s; }
        @keyframes dot-bounce {
          0%, 80%, 100% { transform: scale(0); }
          40% { transform: scale(1); }
        }
        .table-responsive .ant-table {
          border: 1px solid #dee2e6;
        }
        .table-responsive .ant-table-thead > tr > th {
          background: #ffffff;
          font-weight: 600;
          padding: 8px 16px;
          font-size: 14px;
        }
        .table-responsive .ant-table-tbody > tr > td {
          padding: 12px 18px;
          font-size: 14px;
          height: 60px;
        }
        .table-responsive .ant-table-tbody > tr.ant-table-row:hover > td {
          background: #f5f5f5;
        }
      `}</style>
    </div>
  );
};

export default DailyRoomSold;